import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  RefresherEventDetail,
  useIonViewDidEnter,
  useIonViewWillEnter,
} from '@ionic/react';
import { ChatListSkeleton, Container, EmptyChatList, Refresher } from '@mindly/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { useHistory } from 'react-router-dom';
import { EmptyWrapper, RoomsContentWrapper, RoomsWrapper } from './ChatsPage.style';
import useAgoraRtm from '../../context/AgoraContext/AgoraContext';
import { selectIsInfiniteSelector, selectorIsFirstLoading, selectSortedChatRooms } from 'src/redux/chatRooms/selector';
import { userSelector } from '../../redux/user/selector';
import { resetOffset } from 'src/redux/chatRooms/reducer';
import { fetchBroadcastChatRoom, fetchChatRooms } from '../../redux/chatRooms/thunk';
import { selectTotalUnReadMessages } from 'src/redux/chatMessages/selector';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import useLangs from '../../context/LangsContext';
import PageHeader from '../../components/PageHeader/PageHeader';
import SubscriptionStatus from '../../components/SubscriptionStatus';
import HelpButton from '../../components/HelpButton';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import { getUserOnBoardingFlowKeySelector } from '../../redux/onBoardingFlow/selector';
import { FIRST_BOOKING_FAQ } from '../../intercom-articles';
import { usePushNotifications } from '../../shared/context/PushNotificationsContext';
import { useRemoteConfig } from '../../hooks/useRemoteConfig/useRemoteConfig';
import { TranslatedChatListItem } from 'src/translated-components';
import { NeedOnboardingFeature } from '../../features/NeedOnboardingFeature';
import { useTranslation } from 'react-i18next';
import { CHAT_ROOM_TYPES } from '../../redux/chatRooms/types';
import { MindlyCircleLogo52x52 } from '../../shared/assets/icons/mindlyCircleLogo';
import { useAnalytics } from 'src/shared';

export const ChatsPage = () => {
  const { locale } = useLangs();
  const { t } = useTranslation('chat');

  const onboardingKey = useSelector(getUserOnBoardingFlowKeySelector);

  const currentUser = useSelector(userSelector);
  const rooms = useSelector(selectSortedChatRooms);
  const isInfiniteDisabled = useSelector(selectIsInfiniteSelector);
  const isFirstLoading = useSelector(selectorIsFirstLoading);
  const unReadMessages = useSelector(selectTotalUnReadMessages(currentUser?.id ?? ''));
  const lastSubscription = useSelector(lastActiveSubscriptionSelector());
  const pushNotificationsState = usePushNotifications();
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [isEnabledInActiveViewFeature, setIsEnabledInActiveViewFeature] = useState(false);
  const { isActiveFeature } = useRemoteConfig();
  const { isLoading } = useAgoraRtm();
  const { trackPredefinedEvent } = useAnalytics();

  useIonViewDidEnter(() => {
    trackPredefinedEvent('CHAT_LIST_VIEWED', {
      is_new: false,
    });
  }, [trackPredefinedEvent]);

  useIonViewWillEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (currentUser?.id && !isLoading) {
        dispatch(resetOffset());
        await dispatch(fetchBroadcastChatRoom(currentUser.id));
        await dispatch(fetchChatRooms(currentUser.id)).unwrap();
      }
    })();
  }, [currentUser?.id, isLoading]);

  useEffect(() => {
    isActiveFeature('inactive_specialist_view_enabled').then((value) => {
      setIsEnabledInActiveViewFeature(value);
    });
  }, []);

  const loadRoomsData = async (ev: any) => {
    if (!currentUser?.id) {
      ev.target.complete();
      return;
    }
    await dispatch(fetchBroadcastChatRoom(currentUser.id));
    await dispatch(fetchChatRooms(currentUser.id)).unwrap();

    ev.target.complete();
  };

  const refreshRooms = useCallback(
    async (event: CustomEvent<RefresherEventDetail>) => {
      if (!currentUser?.id) {
        event.detail.complete();
        return;
      }
      dispatch(resetOffset());
      await dispatch(fetchBroadcastChatRoom(currentUser.id));
      await dispatch(fetchChatRooms(currentUser.id)).unwrap();
      await pushNotificationsState.updatePermissions();
      event.detail.complete();
    },
    [currentUser?.id, dispatch]
  );

  const containerStyles = useMemo(
    () =>
      !rooms.length
        ? {
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }
        : {
            padding: '0',
          },
    [rooms]
  );

  let content;

  if ((!currentUser?.onBoardingAnswers || !Object.keys(currentUser?.onBoardingAnswers).length) && !rooms?.length) {
    content = (
      <div className={'flex flex-1 justify-center items-center'}>
        <NeedOnboardingFeature
          title={t('chat.empty.title')}
          subtitle={t('chat.empty.description')}
          buttonLabel={t('chat.empty.start_button')}
          onClick={
            onboardingKey
              ? () => history.push(PublicRoutes.ON_BOARDING_FLOW.replace(':flowId', onboardingKey))
              : undefined
          }
        />
      </div>
    );
  } else if (isFirstLoading) {
    content = <ChatListSkeleton />;
  } else {
    content = (
      <RoomsContentWrapper>
        {!rooms.length && (
          <EmptyWrapper>
            <EmptyChatList
              title={intl.formatMessage({
                id: 'chat.empty.title',
                defaultMessage: 'Ви можете надіслати повідомлення своєму психологу',
              })}
              text={intl.formatMessage({
                id: 'chat.empty.text',
                defaultMessage: 'Є питання? Ви можете написати своєму психологу та швидко отримати відповідь.',
              })}
              withButton
              buttonTitle={intl.formatMessage({
                id: 'consultation.empty.btn',
                defaultMessage: 'Вибрати психолога',
              })}
              onClick={() => {
                history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
              }}
            />
            <HelpButton articleId={FIRST_BOOKING_FAQ}>
              <FormattedMessage id={'general.help.how.subscribe'} defaultMessage={'Як записатися на перший сеанс?'} />
            </HelpButton>
          </EmptyWrapper>
        )}
        <RoomsWrapper>
          {rooms.map((room) => (
            <TranslatedChatListItem
              isDisabled={room?.is_blocked_chat && isEnabledInActiveViewFeature}
              key={room.room.id}
              onClick={() => {
                history.push(PrivateRoutes.CHAT_ROOM.replace(':id', room.room.specialist_id));
              }}
              name={room.name
                .split(' ')
                .map((item, index) => (index > 0 ? `${item[0]}.` : item))
                .slice(0, 2)
                .join(' ')}
              message={(room?.last_message?.message as string).split(/<[A-Za-z]>(.*?)<\/[A-Za-z]>/).join('') as string}
              status={
                room?.last_message?.sender_id !== currentUser?.id
                  ? undefined
                  : room?.last_message?.status === 'viewed'
                    ? 'viewed'
                    : 'sent'
              }
              avatar={room.avatar}
              avatarReplacer={
                room.room.type === CHAT_ROOM_TYPES.MINDLY_BROADCAST_ROOM ? <MindlyCircleLogo52x52 /> : undefined
              }
              time={room?.last_message?.updated_at ?? 0}
              timeZone={DateTime.local().zoneName}
              newMessagesCount={unReadMessages.filter((item) => item.room_id === room.room.id).length || undefined}
              lastMessageType={room.last_message.messageType}
              locale={locale}
            />
          ))}
        </RoomsWrapper>
        <IonInfiniteScroll onIonInfinite={loadRoomsData} threshold="100%" disabled={isInfiniteDisabled}>
          <IonInfiniteScrollContent loadingSpinner="crescent" />
        </IonInfiniteScroll>
      </RoomsContentWrapper>
    );
  }

  return (
    <IonPage>
      <PageHeader intercomId={lastSubscription ? undefined : FIRST_BOOKING_FAQ}>
        <SubscriptionStatus />
      </PageHeader>
      <IonContent>
        <Refresher onIonRefresh={refreshRooms} disabled={false} />
        <Container style={{ ...containerStyles }}>{content}</Container>
      </IonContent>
    </IonPage>
  );
};
